import {
  ColScaffold,
  RowScaffold,
  ScaffoldDropZone,
  ScaffoldHeaderCellSpans,
} from "../../scaffold";
import { EditorDefns, TlfViewModeConfig } from "../../utility";
import * as ARS from "../ars_ldm";
import { ArsStateUpdate } from "../contexts";
import { ArsSidebarItemData, ArsSidebarItemKind } from "../sidebar/interface";

export enum ArsTableJustification {
  Left = "left",
  Center = "center",
  Right = "right",
}

export enum ArsTextJustification {
  Default = "default",
  Left = "left",
  Center = "center",
  Right = "right",
  Decimal = "decimal",
}

// #region table groups
export interface ArsTableGroupBase<T = string> {
  id: string;
  dimension?: number;
  hidden?: boolean;
  inlineIndent?: number;
  inlineWithPreviousLevel?: boolean;
  spaceAfterGroup?: boolean;
  label: T;
  parameters?: Record<string, string>;
  subGroups?: ArsTableGroupCalculationDto<T>[];
}

export interface ArsTableOperation<T = string> extends ArsTableGroupBase<T> {
  decimalAlignPercent?: number;
  justification?: ArsTextJustification;
  operation: ARS.Operation;
  operationId: string;
  targetVariable: string;
  caseWhenCondition?: ARS.CaseWhenCondition<T>;
}

// TODO: These don't really need to be called "SubGroup" anymore,
// since subGroups have been added to ArsTableOperation
export interface ArsTableSubGroupAnalysisMethod<T = string> extends ArsTableGroupBase<T> {
  analysisMethod: ARS.AnalysisMethod;
  analysisMethodId: string;
  hiddenOperationIds?: string[];
  inlineRowHeaders?: boolean;
  subGroups: ArsTableGroupCalculationDto<T>[];
}

export interface ArsTableSubGroupGroup<T = string> extends ArsTableGroupBase<T> {
  group: ARS.Group;
  groupId: string;
  inlineRowHeaders?: boolean;
  isDataDriven?: boolean;
}

export interface ArsTableSubGroupGroupingFactor<T = string> extends ArsTableGroupBase<T> {
  groupingFactor: ARS.GroupingFactor;
  groupingFactorId: string;
  hiddenGroupIds?: string[];
  inHeaderOperation?: ARS.Operation;
  inlineRowHeaders?: boolean;
  isPlaceholder?: boolean;
  spaceAfterSubGroups?: boolean;
}

export type ArsTableSubGroup<T = string> =
  ArsTableSubGroupAnalysisMethod<T> | ArsTableSubGroupGroup<T> | ArsTableSubGroupGroupingFactor<T>;

export type ArsTableGroupCalculationDto<T = string> = ArsTableSubGroup<T> | ArsTableOperation<T>;
// #endregion

// #region Cell types
export const defaultColumnWidth = 120;
export const defaultRowHeight = 40;

export enum ArsTableCellType {
  ColumnGroupCell = "columnGroupCell",
  ColumnGroupLabel = "columnGroupLabel",
  ColumnOperation = "columnOperation",
  DataCell = "dataCell",
  RowGroupCell = "rowGroupCell",
  RowGroupLabel = "rowGroupLabel",
  RowHeader = "rowHeader",
  RowOperation = "rowOperation",
}

export interface ArsTableCellBase<T = string> {
  annotationContent: string;
  cellType: ArsTableCellType;
  columnId: string;
  columnIndex: number;
  dataPath: string;
  disabled?: boolean;
  displayContent: T;
  hidden?: boolean;
  inlineIndent?: number;
  justification: ArsTextJustification;
  rowId: string;
  targetType: string;
}

export interface ArsTableColumnGroupCell<T = string> extends ArsTableCellBase<T> {
  cellType: ArsTableCellType.ColumnGroupCell;
  colSpan?: number;
  rowSpan?: number;
  siblingCount: number;
  targetId: string;
  targetType: "groupingFactor" | "analysisMethod";
  width?: number;
}

export interface ArsTableColumnGroupLabel<T = string> extends ArsTableCellBase<T> {
  cellType: ArsTableCellType.ColumnGroupLabel;
  colSpan?: number;
  rowSpan?: number;
  siblingCount: number;
  targetId: string;
  targetType: "group" | "dataDriven";
  width?: number;
}

export interface ArsTableColumnOperationCell<T = string> extends ArsTableCellBase<T> {
  cellType: ArsTableCellType.ColumnOperation;
  targetId: string;
  targetType: "operation";
  width?: number;
}

export interface ArsTableDataCell<T = string> extends ArsTableCellBase<T> {
  cellType: ArsTableCellType.DataCell;
  decimalAlignPercent?: number;
  targetType: "data";
}

export interface ArsTableRowGroupCell<T = string> extends ArsTableCellBase<T> {
  cellType: ArsTableCellType.RowGroupCell;
  colSpan?: number;
  height?: number;
  indents?: number;
  rowSpan?: number;
  siblingCount: number;
  targetId: string;
  targetType: "groupingFactor" | "analysisMethod";
}

export interface ArsTableRowGroupLabel<T = string> extends ArsTableCellBase<T> {
  cellType: ArsTableCellType.RowGroupLabel;
  colSpan?: number;
  height?: number;
  indents?: number;
  rowSpan?: number;
  siblingCount: number;
  targetId: string;
  targetType: "group" | "dataDriven";
}

export interface ArsTableRowHeaderCell<T = string> extends ArsTableCellBase<T> {
  cellType: ArsTableCellType.RowHeader;
  rowSpan?: number;
  targetType: "rowHeader";
  width?: number;
}

export interface ArsTableRowOperationCell<T = string> extends ArsTableCellBase<T> {
  cellType: ArsTableCellType.RowOperation;
  height?: number;
  hidden?: boolean;
  indents?: number;
  targetId: string;
  targetType: "operation";
}

/**
 * Cell types that represent row-header hierarchy cells.
 */
export const arsTableRowHeaderCellTypes = new Set<ArsTableCellType>([
  ArsTableCellType.RowGroupCell,
  ArsTableCellType.RowGroupLabel,
  ArsTableCellType.RowOperation,
]);

/**
 * Cell types that represent column-header hierarchy cells.
 */
export const arsTableColumnHeaderCellTypes = new Set<ArsTableCellType>([
  ArsTableCellType.ColumnGroupCell,
  ArsTableCellType.ColumnGroupLabel,
  ArsTableCellType.ColumnOperation,
]);

/**
 * Cell types that represent the "box" area in the top-left of the table
 * which is outside both row and column hierarchies.
 */
export const arsTableBoxCellTypes = new Set<ArsTableCellType>([ArsTableCellType.RowHeader]);

/**
 * Cell lable types
 */
export const areTableHeaderLabelCellTypes = new Set<ArsTableCellType>([
  ArsTableCellType.ColumnGroupLabel,
  ArsTableCellType.RowGroupLabel,
]);

export const arsTableHeaderCellTypes = new Set<ArsTableCellType>([
  ...arsTableColumnHeaderCellTypes,
  ...arsTableRowHeaderCellTypes,
]);

export type ArsTableCellWithTarget<T = string> =
  | ArsTableColumnGroupCell<T>
  | ArsTableColumnGroupLabel<T>
  | ArsTableColumnOperationCell<T>
  | ArsTableRowGroupCell<T>
  | ArsTableRowGroupLabel<T>
  | ArsTableRowOperationCell<T>;

export type ArsTableHeaderCell<T = string> =
  | ArsTableColumnGroupCell<T>
  | ArsTableColumnGroupLabel<T>
  | ArsTableColumnOperationCell<T>
  | ArsTableRowHeaderCell<T>;

export type ArsTableBodyCell<T = string> =
  | ArsTableDataCell<T>
  | ArsTableRowGroupCell<T>
  | ArsTableRowGroupLabel<T>
  | ArsTableRowOperationCell<T>;

export type ArsTableCellData<T = string> = ArsTableBodyCell<T> | ArsTableHeaderCell<T>;
// #endregion

// #region Table Row and Body types
export interface ArsTableHeaderRow<T = string> {
  arsTableCells: ArsTableHeaderCell<T>[];
  height: number;
}

export interface ArsTableBodyRow<T = string> {
  arsTableCells: ArsTableBodyCell<T>[];
  height: number;
  rowGroup: number;
  spaceAfterGroup?: boolean;
}

export type ArsTableRow<T = string> = ArsTableHeaderRow<T> | ArsTableBodyRow<T>;

/**
 * Represents a single column in the row-header area of an ARS table.
 * Stores the column label, per-body-row heights, and column width.
 */
export interface ArsTableRowHeader<T = string> {
  heights: number[];
  label: T;
  width: number;
}

export interface ArsTableBody<T = string> extends Record<
  string,
  string | undefined | ArsTableGroupCalculationDto<T>[] | ArsTableRowHeader<T>[]
> {
  id: string;
  analysisMethodId: string;
  bodyType: "arsTable";
  columns: ArsTableGroupCalculationDto<T>[];
  dataSubsetId?: string;
  rowHeaders: ArsTableRowHeader<T>[];
  rows: ArsTableGroupCalculationDto<T>[];
}
// #endregion

/**
 * Derived scaffold model used by ArsTableContent for rendering.
 */
export interface ArsTableScaffoldModel<T = string> extends Record<
  string,
  | Record<string, Record<string, ArsTableCellData<T>>>
  | ColScaffold<ArsTableCellData<T>>[]
  | RowScaffold<ArsTableCellData<T>>[]
  | ScaffoldHeaderCellSpans
> {
  cellData: Record<string, Record<string, ArsTableCellData<T>>>;
  columns: ColScaffold<ArsTableCellData<T>>[];
  rows: RowScaffold<ArsTableCellData<T>>[];
  headerColSpans: ScaffoldHeaderCellSpans;
}

/**
 * Selected item metadata shown by the ArsTable item properties panel.
 */
export interface ArsTableViewItem {
  id: string;
  type: string;
  dataPath: string;
}

export interface ArsTableSettings<T = string> {
  id: string;
  disabled: boolean;
  editorProps: EditorDefns<T>;
  justification: ArsTableJustification;
  sidebarStyle?: React.CSSProperties;
  style?: React.CSSProperties;
  tlfViewMode: TlfViewModeConfig;
}

/**
 * Persisted table body, current method metadata and transient editing state.
 */
export interface ArsTableState<T = string> {
  /** Current ARS-context method selected by bodyData.analysisMethodId. */
  analysisMethod?: ARS.AnalysisMethod;
  bodyData: ArsTableBody<T>;
  scaffoldModel: ArsTableScaffoldModel<T> | null;
  calculatedTableRows: ArsTableRow<T>[] | null;
  changed: boolean;
  settings: ArsTableSettings<T>;
  usedGroupingFactorIds: Set<string>;
  viewItem?: ArsTableViewItem;
}

/**
 *  Action types for ArsTable reducer
 */
export const ARST_CHANGE_SENT = "ARST_CHANGE_SENT";
export const ARST_LOAD_DATA = "ARST_LOAD_DATA";
export const ARST_PATCH_DATA = "ARST_PATCH_DATA";
export const ARST_SET_ANALYSIS_METHOD = "ARST_SET_ANALYSIS_METHOD";
export const ARST_SET_SCAFFOLD_COL_WIDTH = "ARST_SET_SCAFFOLD_COL_WIDTH";
export const ARST_SET_SCAFFOLD_ROW_HEIGHT = "ARST_SET_SCAFFOLD_ROW_HEIGHT";
export const ARST_SET_SIDEBAR_FILTER = "ARST_SET_SIDEBAR_FILTER";
export const ARST_SET_SIDEBAR_VIEW = "ARST_SET_SIDEBAR_VIEW";
export const ARST_SET_TLF_VIEW_MODE = "ARST_SET_TLF_VIEW_MODE";
export const ARST_SET_VIEW_ITEM = "ARST_SET_VIEW_ITEM";
export const ARST_UPDATE_SCAFFOLD = "ARST_UPDATE_SCAFFOLD";

export interface ArsTableCellActionConfig {
  dropActions: ArsTableDropActionDefinition[];
  contextMenuActions: ArsTableContextMenuActionDefinition[];
}

export interface ArstActionChangeSent {
  operation: typeof ARST_CHANGE_SENT;
}

export interface ArstActionLoadData<T = string> {
  operation: typeof ARST_LOAD_DATA;
  bodyData: ArsTableBody<T>;
}

/**
 * Refreshes the table's method definition without changing its persisted body.
 */
export interface ArstActionSetAnalysisMethod {
  operation: typeof ARST_SET_ANALYSIS_METHOD;
  analysisMethod: ARS.AnalysisMethod | undefined;
}

export interface ArstActionPatchData extends ArsStateUpdate {
  operation: typeof ARST_PATCH_DATA;
}
export interface ArstActionPatchDataMultiple {
  operation: typeof ARST_PATCH_DATA;
  /** Multiple updates for atomic application */
  updates: ArsStateUpdate[];
}

export interface ArstActionSetScaffoldColWidth {
  operation: typeof ARST_SET_SCAFFOLD_COL_WIDTH;
  colId: string;
  width: number;
}

export interface ArstActionSetScaffoldRowHeight {
  operation: typeof ARST_SET_SCAFFOLD_ROW_HEIGHT;
  rowId: string;
  height: number;
}

export interface ArstActionSetTlfViewMode {
  operation: typeof ARST_SET_TLF_VIEW_MODE;
  tlfViewMode: TlfViewModeConfig;
}

export interface ArstActionSetViewItem {
  operation: typeof ARST_SET_VIEW_ITEM;
  viewItem: ArsTableViewItem | undefined;
}

export interface ArstActionUpdateScaffold<T = string> {
  operation: typeof ARST_UPDATE_SCAFFOLD;
  calculatedTableRows: ArsTableRow<T>[];
}

/**
 * Context-menu actions
 */
export const ARST_REMOVE_GROUPINGFACTOR = "ARST_REMOVE_GROUPINGFACTOR";
export const ARST_REMOVE_OPERATION = "ARST_REMOVE_OPERATION";
export const ARST_TOGGLE_GROUP_LABEL_INLINE = "ARST_TOGGLE_GROUP_LABEL_INLINE";
export const ARST_TOGGLE_GROUP_LABEL_VISIBILITY = "ARST_TOGGLE_GROUP_LABEL_VISIBILITY";
export const ARST_TOGGLE_INLINE_WITH_PREVIOUS_LEVEL = "ARST_TOGGLE_INLINE_WITH_PREVIOUS_LEVEL";
export const ARST_TOGGLE_SPACE_AFTER_GROUP = "ARST_TOGGLE_SPACE_AFTER_GROUP";
export const ARST_TOGGLE_SPACE_AFTER_SUBGROUPS = "ARST_TOGGLE_SPACE_AFTER_SUBGROUPS";
export type ArstContextMenuOperation =
  | typeof ARST_REMOVE_GROUPINGFACTOR
  | typeof ARST_REMOVE_OPERATION
  | typeof ARST_TOGGLE_GROUP_LABEL_INLINE
  | typeof ARST_TOGGLE_GROUP_LABEL_VISIBILITY
  | typeof ARST_TOGGLE_INLINE_WITH_PREVIOUS_LEVEL
  | typeof ARST_TOGGLE_SPACE_AFTER_GROUP
  | typeof ARST_TOGGLE_SPACE_AFTER_SUBGROUPS;

export interface ArsTableContextMenuActionDefinition {
  label: string;
  operation: ArstContextMenuOperation;
  newValue?: unknown;
  disabled?: boolean;
}

export interface ArstActionContextmenuAction<T = string> {
  operation: ArstContextMenuOperation;
  cellValue: ArsTableCellData<T>;
  newValue?: unknown;
}

/**
 * Drop actions
 */
export const ARST_INSERT_COLUMN_AFTER = "ARST_INSERT_COLUMN_AFTER";
export const ARST_INSERT_COLUMN_BEFORE = "ARST_INSERT_COLUMN_BEFORE";
export const ARST_INSERT_ROW_AFTER = "ARST_INSERT_ROW_AFTER";
export const ARST_INSERT_ROW_BEFORE = "ARST_INSERT_ROW_BEFORE";
export const ARST_INSERT_OPERATION_COLUMN_AFTER = "ARST_INSERT_OPERATION_COLUMN_AFTER";
export const ARST_INSERT_OPERATION_COLUMN_BEFORE = "ARST_INSERT_OPERATION_COLUMN_BEFORE";
export const ARST_INSERT_OPERATION_ROW_AFTER = "ARST_INSERT_OPERATION_ROW_AFTER";
export const ARST_INSERT_OPERATION_ROW_BEFORE = "ARST_INSERT_OPERATION_ROW_BEFORE";
export const ARST_INSERT_OPERATION_SUBGROUP = "ARST_INSERT_OPERATION_SUBGROUP";
export const ARST_INSERT_SUBGROUP = "ARST_INSERT_SUBGROUP";
export type ArstDropOperation =
  | typeof ARST_INSERT_COLUMN_AFTER
  | typeof ARST_INSERT_COLUMN_BEFORE
  | typeof ARST_INSERT_ROW_AFTER
  | typeof ARST_INSERT_ROW_BEFORE
  | typeof ARST_INSERT_OPERATION_COLUMN_AFTER
  | typeof ARST_INSERT_OPERATION_COLUMN_BEFORE
  | typeof ARST_INSERT_OPERATION_ROW_AFTER
  | typeof ARST_INSERT_OPERATION_ROW_BEFORE
  | typeof ARST_INSERT_OPERATION_SUBGROUP
  | typeof ARST_INSERT_SUBGROUP;

export interface ArsTableDropActionDefinition {
  operation: ArstDropOperation;
  droppedDataKinds: ArsSidebarItemKind[];
  dropZones: Set<ScaffoldDropZone>;
}

export interface ArstActionDropAction {
  operation: ArstDropOperation;
  rowId: string;
  columnId: string;
  payload: ArsSidebarItemData[];
  zone: ScaffoldDropZone;
}

export type ArsTableAction<T = string> =
  | ArstActionChangeSent
  | ArstActionContextmenuAction<T>
  | ArstActionDropAction
  | ArstActionLoadData<T>
  | ArstActionPatchData
  | ArstActionPatchDataMultiple
  | ArstActionSetAnalysisMethod
  | ArstActionSetScaffoldColWidth
  | ArstActionSetScaffoldRowHeight
  | ArstActionSetTlfViewMode
  | ArstActionSetViewItem
  | ArstActionUpdateScaffold<T>;
